/**
 * InsufficientEvidencePanel — T8 doctrine surface.
 *
 * Renders when `doc.evidence_quality === "insufficient"` (the
 * backend-locked verdict). When this panel is the assessment
 * surface, the parent MUST suppress all judgement surfaces:
 *   • score chips
 *   • competency scores
 *   • pass/fail wording
 *   • readiness labels
 *   • confidence labels
 *   • skill-detected / competency-observed lines
 *   • improvement tips that imply assessment
 *
 * Allowed alongside this panel:
 *   • raw video / clip metadata
 *   • raw transcript
 *
 * Doctrine pin:
 *   "If evidence is insufficient, the system may describe the
 *    evidence problem, but it must not assess the driver,
 *    instructor, PDI, skill level, readiness, competence, or
 *    improvement priority. Raw proof can remain visible.
 *    Judgement must be suppressed."
 */
import React from "react";
import { AlertTriangle } from "lucide-react";

const DEFAULT_EXPLANATION =
  "We detected conversation during a continuous drive, but not " +
  "enough reliable driving evidence to produce a fair assessment. " +
  "The review below is intentionally calm and unspecific — try " +
  "re-uploading with the camera capturing clearer driving moments, " +
  "or pick a different recording with more observable decisions, " +
  "manoeuvres or hazards.";

export default function InsufficientEvidencePanel({ explanation, analysisIntent }) {
  const copy = (explanation && explanation.trim()) || DEFAULT_EXPLANATION;
  return (
    <section
      data-testid="lr-insufficient-evidence"
      data-evidence-quality="insufficient"
      data-analysis-intent={analysisIntent || undefined}
      className="rounded-2xl border border-slate-700/40 bg-slate-900/60 p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-500/15 flex items-center justify-center border border-slate-500/30 shrink-0">
          <AlertTriangle className="w-5 h-5 text-slate-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p
            data-testid="lr-insufficient-evidence-heading"
            className="text-[10px] uppercase tracking-widest font-extrabold text-slate-400"
          >
            Evidence insufficient
          </p>
          <p className="text-base font-extrabold text-white mt-0.5 leading-tight">
            No assessment for this recording
          </p>
          <p
            data-testid="lr-insufficient-evidence-explanation"
            className="text-[13px] text-slate-300 mt-2 leading-relaxed"
          >
            {copy}
          </p>
          <p className="text-[11px] text-slate-500 mt-3">
            Raw video and transcript remain available below for
            reference, but no score, competency, readiness or
            improvement guidance is appropriate here.
          </p>
        </div>
      </div>
    </section>
  );
}
